import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getShiftAssignments,
    getShifts,
    getShiftEnums,
    deleteShiftAssignment,
} from '../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { PaginationLimit } from '../../../components/PaginationLimit';
import SearchableDropdown from '../../../components/SearchableDropdown';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import {
    toBool,
    formatDateOnly,
    readableTextOn,
    shiftTimeRange,
    offDayLabel,
} from '../../../consts/shiftConstants';
import AssignmentFormModal from './Component/AssignmentFormModal';
import ChangeShiftModal from './Component/ChangeShiftModal';
import EndAssignmentModal from './Component/EndAssignmentModal';

// Who works which shift, from when.
//
// An assignment IS the schedule — there is no roster or day-by-day calendar
// behind it. Most assigning happens on the employee form, which closes the
// previous assignment automatically; this screen handles what that form
// refuses: future-dated assignments, back-dated fixes, and shift history.

const selectClass = 'tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500';

const ShiftAssignments = () => {
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const canAssign = can(PERMISSIONS.SHIFT_ASSIGN);
    const societyId = selectedSocietyStorer.getSocietyId();

    const { assignments, assignmentsPagination, assignmentsLoading, shifts } = useSelector(state => state.ShiftReducer || {});
    const { employeesDropdownData, employeesDropdownLoading } = useSelector(state => state.EmployeeReducer || {});

    const [employeeId, setEmployeeId] = useState('');
    const [shiftId, setShiftId] = useState('');
    const [activeOn, setActiveOn] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [refreshTick, setRefreshTick] = useState(0);

    const [formAssignment, setFormAssignment] = useState(null); // null = closed, {} = new
    const [changeTarget, setChangeTarget] = useState(null);
    const [endTarget, setEndTarget] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const refresh = () => setRefreshTick(t => t + 1);

    useEffect(() => {
        dispatch(getShiftEnums());
        // The pickers need every shift, not a page of them.
        dispatch(getShifts({ limit: 100 }));
        if (societyId) dispatch(getEmployeesDropdown({ societyId }));
    }, [dispatch, societyId]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getShiftAssignments({
            employee_id: employeeId || undefined,
            shift_id: shiftId || undefined,
            active_on: activeOn || undefined,
            is_active: activeFilter === '' ? undefined : activeFilter,
            page,
            limit,
        }));
    }, [dispatch, employeeId, shiftId, activeOn, activeFilter, page, limit, refreshTick, refreshKey]);

    const employeeOptions = useMemo(() => (
        (Array.isArray(employeesDropdownData) ? employeesDropdownData : []).map(e => ({
            value: String(e.employee_id),
            label: [e.first_name, e.last_name].filter(Boolean).join(' ').trim() || e.email || `Employee #${e.employee_id}`,
        }))
    ), [employeesDropdownData]);

    const shiftOptions = useMemo(() => (
        (shifts || []).map(s => ({ value: String(s.shift_id), label: `${s.shift_code} · ${s.shift_name}` }))
    ), [shifts]);

    const handleDelete = (assignment) => {
        dispatch(deleteShiftAssignment({
            assignment_id: assignment.assignment_id,
            onSuccess: () => { setConfirmDelete(null); refresh(); },
        }));
    };

    const pg = assignmentsPagination || {};

    return (
        <>
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4 tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Shift Assignments</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                        Who works which shift, and from when · assign from the employee form for the simple cases
                    </p>
                </div>
                {canAssign && (
                    <button
                        type="button"
                        onClick={() => setFormAssignment({})}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Assign shift
                    </button>
                )}
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Employee</label>
                        <SearchableDropdown
                            value={employeeId}
                            onChange={(v) => { setEmployeeId(v === 'all' ? '' : v); setPage(1); }}
                            options={employeeOptions}
                            loading={employeesDropdownLoading}
                            placeholder="All employees"
                            small
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Shift</label>
                        <SearchableDropdown
                            value={shiftId}
                            onChange={(v) => { setShiftId(v === 'all' ? '' : v); setPage(1); }}
                            options={shiftOptions}
                            placeholder="All shifts"
                            small
                        />
                    </div>
                    <div>
                        {/* active_on also matches open-ended assignments, which a
                            plain effective_to filter would miss. */}
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Covering the date</label>
                        <input type="date" value={activeOn} onChange={(e) => { setActiveOn(e.target.value); setPage(1); }} className={selectClass} />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Status</label>
                        <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }} className={selectClass}>
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                </div>
                {(employeeId || shiftId || activeOn || activeFilter) && (
                    <button
                        type="button"
                        onClick={() => { setEmployeeId(''); setShiftId(''); setActiveOn(''); setActiveFilter(''); setPage(1); }}
                        className="tw-mt-3 tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:refresh" className="tw-text-sm" /> Reset
                    </button>
                )}
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {['Employee', 'Shift', 'Effective', 'Weekly offs', 'Notes', 'Status', 'Actions'].map((h) => (
                                    <th key={h} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {assignmentsLoading ? (
                                <tr><td colSpan={7} className="tw-text-center tw-py-12"><Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" /></td></tr>
                            ) : (assignments || []).length === 0 ? (
                                <tr><td colSpan={7} className="tw-text-center tw-py-12">
                                    <Icon icon="hugeicons:user-switch" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2 tw-mb-0">No assignments for these filters</p>
                                </td></tr>
                            ) : (
                                assignments.map(a => {
                                    const name = [a.employee?.first_name, a.employee?.last_name].filter(Boolean).join(' ').trim()
                                        || `Employee #${a.employee_id}`;
                                    return (
                                        <tr key={a.assignment_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                            <td className="tw-px-3 tw-py-2">
                                                <div className="tw-font-medium tw-text-gray-800">{name}</div>
                                                <div className="tw-text-[10px] tw-text-gray-400">
                                                    {a.employee?.employee_code}
                                                    {a.employee?.designation ? ` · ${a.employee.designation}` : ''}
                                                </div>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <span
                                                    className="tw-inline-block tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold"
                                                    style={{
                                                        backgroundColor: a.shift?.color_hex || '#E5E7EB',
                                                        color: readableTextOn(a.shift?.color_hex),
                                                    }}
                                                >
                                                    {a.shift?.shift_code || a.shift_id}
                                                </span>
                                                <div className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5">{shiftTimeRange(a.shift)}</div>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">
                                                {formatDateOnly(a.effective_from)}
                                                {' → '}
                                                {a.effective_to
                                                    ? formatDateOnly(a.effective_to)
                                                    : <span className="tw-text-emerald-600">open-ended</span>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-600">{offDayLabel(a)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-max-w-[160px]">
                                                <span className="tw-block tw-truncate" title={a.notes || ''}>{a.notes || '—'}</span>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${toBool(a.is_active) ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-500'}`}>
                                                    {toBool(a.is_active) ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                {canAssign && (
                                                    <div className="tw-flex tw-gap-1 tw-flex-wrap">
                                                        <button
                                                            type="button"
                                                            onClick={() => setChangeTarget(a)}
                                                            className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-blue-700 tw-bg-blue-50 hover:tw-bg-blue-100"
                                                            title="End this assignment and start a new one"
                                                        >
                                                            Change shift
                                                        </button>
                                                        {!a.effective_to && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setEndTarget(a)}
                                                                className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-amber-700 tw-bg-amber-50 hover:tw-bg-amber-100"
                                                            >
                                                                End
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => setFormAssignment(a)}
                                                            className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfirmDelete(a)}
                                                            className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-red-700 tw-bg-red-50 hover:tw-bg-red-100"
                                                            title="Only for an assignment created by mistake"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pg.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pg.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}

            <AssignmentFormModal
                isOpen={Boolean(formAssignment)}
                assignment={formAssignment}
                onClose={() => setFormAssignment(null)}
                onDone={refresh}
            />
            <ChangeShiftModal
                assignment={changeTarget}
                onClose={() => setChangeTarget(null)}
                onDone={refresh}
            />
            <EndAssignmentModal
                assignment={endTarget}
                onClose={() => setEndTarget(null)}
                onDone={refresh}
            />

            {/* Hard delete, so it erases the record that this period was worked
                on this shift. /end is the right tool for anything real. */}
            {confirmDelete && (
                <div className="tw-fixed tw-inset-0 tw-z-[9000] tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/50" onClick={() => setConfirmDelete(null)}>
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-w-full tw-max-w-sm tw-p-5" onClick={(e) => e.stopPropagation()}>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-2">Delete this assignment?</h3>
                        <p className="tw-text-xs tw-text-gray-600 tw-mb-1">
                            Use this only for an assignment created by mistake. To stop a shift that has already been worked, end it instead.
                        </p>
                        <p className="tw-text-[11px] tw-text-gray-400 tw-mb-4">
                            This cannot be undone.
                        </p>
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button type="button" onClick={() => setConfirmDelete(null)} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-600 tw-text-xs">Cancel</button>
                            <button type="button" onClick={() => handleDelete(confirmDelete)} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-red-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-red-700">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShiftAssignments;
